import {useState} from 'react';
import {useQuery, useMutation} from '@apollo/client';
import {Table, Pagination, Spin, Tag, Space, Button} from 'antd';
import {
  GET_ALL_MINI_FEEDBACKS,
  DELETE_MINI_FEEDBACK,
  TOGGLE_MINI_FEEDBACK_STATUS,
} from './resolvers';
import styles from './miniFeedback.module.scss';
import pageNotFound from 'images/pageNotFound.png';
import DBModal from 'components/modal/modal';
import {CreateMiniFeedback} from './CreateMiniFeedback';
import {UpdateMiniFeedback} from './UpdateMiniFeedback';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import {NavTabsEnum} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';

export const MiniFeedback = () => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [updateModal, setUpdateModal] = useState({isOpen: false, data: null});
  const [deleteModal, setDeleteModal] = useState({isOpen: false, data: null});
  const [statusModal, setStatusModal] = useState({isOpen: false, data: null});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.FAQ),
  );

  const {data, loading, refetch} = useQuery(GET_ALL_MINI_FEEDBACKS, {
    variables: {
      input: {
        pagination: {
          page: currentPage,
          limit: limit,
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const [deleteMiniFeedback, {loading: deleteLoading}] =
    useMutation(DELETE_MINI_FEEDBACK);

  const [toggleMiniFeedbackStatus, {loading: toggleLoading}] = useMutation(
    TOGGLE_MINI_FEEDBACK_STATUS,
  );

  const handlePageChange = page => {
    setCurrentPage(page);
    refetch({
      input: {
        pagination: {
          page,
          limit,
        },
      },
    });
  };

  const onShowSizeChange = (current, size) => {
    setLimit(size);
    // When size chages, typically you want to reset to page 1 to avoid being out of bounds
    setCurrentPage(1);
    refetch({
      input: {
        pagination: {
          page: 1,
          limit: size,
        },
      },
    });
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Feature',
      dataIndex: 'featureId',
      key: 'featureId',
      render: text => <h4>{text?.replace(/_/g, ' ')}</h4>,
    },
    {
      title: 'Questions',
      dataIndex: 'totalQuestions',
      key: 'totalQuestions',
    },
    {
      title: 'Responses',
      dataIndex: 'totalResponses',
      key: 'totalResponses',
    },
    {
      title: 'Triggers',
      dataIndex: 'triggers',
      key: 'triggers',
      render: triggers => (
        <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
          <span style={{fontSize: 13, color: '#4b5563'}}>
            <b>{triggers?.timeOnFeatureThreshold || 0}</b> minutes on feature
          </span>
          <span style={{fontSize: 13, color: '#4b5563'}}>
            <b>{triggers?.clickThreshold || 0}</b> feature clicks
          </span>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: isActive => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <a
            disabled={!tabOperations?.update}
            onClick={e => {
              e.stopPropagation();
              setUpdateModal({isOpen: true, data: record});
            }}>
            Update
          </a>
          <a
            disabled={!tabOperations?.update}
            onClick={e => {
              e.stopPropagation();
              setStatusModal({isOpen: true, data: record});
            }}>
            {record.isActive ? 'Deactivate' : 'Activate'}
          </a>
          <a
            style={{color: 'red'}}
            disabled={!tabOperations?.delete}
            onClick={e => {
              e.stopPropagation();
              setDeleteModal({isOpen: true, data: record});
            }}>
            Delete
          </a>
        </Space>
      ),
    },
  ];

  const responseData = data?.getAllMiniFeedbacks;

  if (loading && !responseData) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  // Handle GraphQLError inside data payload
  if (responseData?.__typename === 'Error') {
    return (
      <div className={styles.error}>
        <h1>{responseData.message}</h1>
      </div>
    );
  }

  const feedbacks = responseData?.data || [];
  const total = responseData?.pagination?.total || 0;

  const noUpdates = (
    <div className={styles['no-updates']}>
      <img src={pageNotFound} alt="page not found" />
      <h1>No Feedback Found</h1>
      <span>There is currently no Feature Feedback available</span>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Feature Feedback</h1>
        <Button
          type="primary"
          disabled={!tabOperations?.create}
          style={{
            background: '#8b5cf6',
            borderColor: '#8b5cf6',
            height: '40px',
            borderRadius: '8px',
            padding: '0 24px',
            fontWeight: 500,
          }}
          onClick={() => setOpenModal(true)}>
          Add New Feature Feedback
        </Button>
      </div>
      <div className={styles.tableContainer}>
        {feedbacks.length >= 1 ? (
          <div>
            <Table
              dataSource={feedbacks}
              columns={columns}
              pagination={false}
              rowKey="id"
              className="ant-table"
              onRow={record => ({
                onClick: () => {
                  navigate(
                    `/dashboard/support/faq/mini-feedback/${record.featureId}/${record.id}`,
                  );
                },
                style: {cursor: 'pointer'},
              })}
            />
            <Pagination
              showQuickJumper
              current={currentPage}
              showSizeChanger
              onChange={handlePageChange}
              className="ant-table-pagination"
              total={total}
              pageSize={limit}
              onShowSizeChange={onShowSizeChange}
              style={{marginTop: 20, textAlign: 'right'}}
            />
          </div>
        ) : (
          noUpdates
        )}
      </div>
      <DBModal
        isOpen={openModal}
        handleClose={() => setOpenModal(false)}
        width={900}
        content={
          <CreateMiniFeedback
            closeModal={() => setOpenModal(false)}
            refetch={() => {
              setCurrentPage(1);
              refetch({
                input: {
                  pagination: {
                    page: 1,
                    limit: limit,
                  },
                },
              });
            }}
          />
        }
      />
      {updateModal.isOpen && (
        <DBModal
          isOpen={updateModal.isOpen}
          handleClose={() => setUpdateModal({isOpen: false, data: null})}
          width={900}
          content={
            <UpdateMiniFeedback
              feedback={updateModal.data}
              closeModal={() => setUpdateModal({isOpen: false, data: null})}
              refetch={() => {
                refetch({
                  input: {
                    pagination: {
                      page: currentPage,
                      limit: limit,
                    },
                  },
                });
              }}
            />
          }
        />
      )}
      <ConfirmModal
        openModal={deleteModal.isOpen}
        setOpenModal={() => setDeleteModal({isOpen: false, data: null})}
        title="Delete Feature Feedback"
        loading={deleteLoading}
        confirmText={
          <p>
            Are you sure you want to delete this mini feedback? This action
            cannot be undone.
          </p>
        }
        handleOperation={() => {
          deleteMiniFeedback({
            variables: {
              input: {
                id: deleteModal.data?.id,
              },
            },
          })
            .then(({data: deleteData}) => {
              if (deleteData?.deleteMiniFeedback?.__typename === 'Error') {
                toast.error(deleteData.deleteMiniFeedback.message);
              } else {
                toast.success(
                  deleteData?.deleteMiniFeedback?.message ||
                    'Deleted successfully',
                );
                setDeleteModal({isOpen: false, data: null});
                refetch();
              }
            })
            .catch(error => {
              toast.error(error.message);
            });
        }}
      />
      <ConfirmModal
        openModal={statusModal.isOpen}
        setOpenModal={() => setStatusModal({isOpen: false, data: null})}
        title={`${
          statusModal.data?.isActive ? 'Deactivate' : 'Activate'
        } Feature Feedback`}
        loading={toggleLoading}
        confirmText={
          <p>
            Are you sure you want to{' '}
            {statusModal.data?.isActive ? 'deactivate' : 'activate'} this
            feature feedback?
          </p>
        }
        handleOperation={() => {
          toggleMiniFeedbackStatus({
            variables: {
              input: {
                id: statusModal.data?.id,
                isActive: !statusModal.data?.isActive,
              },
            },
          })
            .then(({data: toggleData}) => {
              if (
                toggleData?.toggleMiniFeedbackStatus?.__typename === 'Error'
              ) {
                toast.error(toggleData.toggleMiniFeedbackStatus.message);
              } else {
                toast.success(
                  toggleData?.toggleMiniFeedbackStatus?.message ||
                    'Status updated successfully',
                );
                setStatusModal({isOpen: false, data: null});
                refetch();
              }
            })
            .catch(error => {
              toast.error(error.message);
            });
        }}
      />
    </div>
  );
};
